import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { Draft, ID } from '@proofroom/schema';
import type { SendChangesResult } from '../api/endpoints';

export interface PublishBannerProps {
  draft: Draft;
  documentId: string;
  /** the draft the room's unsent edits, if any, belong to: the one the client is currently on */
  currentDraftNumber: number;
  dirtyCount: number;
  sending: boolean;
  onSend(): Promise<SendChangesResult>;
  /** identifies a conflict's paragraph the way the rail already does, via its story */
  paraLabel(paraId: ID): string;
  onDismiss(): void;
}

/**
 * A clean room opens the published draft straight away. A dirty one shows a guard instead of
 * discarding unsent edits silently: send them first, discard them, or stay put. A send that comes
 * back with conflicts (another author's proposal still sits on a paragraph this one touched) does
 * not auto-switch either, since the switch would unmount this card before anyone could read them.
 */
export function PublishBanner({ draft, documentId, currentDraftNumber, dirtyCount, sending, onSend, paraLabel, onDismiss }: PublishBannerProps) {
  const nav = useNavigate();
  const [guarding, setGuarding] = useState(false);
  const [sent, setSent] = useState<SendChangesResult | null>(null);

  const openDraft = () => {
    onDismiss();
    nav(`/documents/${documentId}/drafts/${draft.id}`);
  };
  const onOpenClick = () => { if (dirtyCount > 0) setGuarding(true); else openDraft(); };
  const onSendThenOpen = () => {
    void onSend().then((result) => {
      if (result.conflicts.length > 0) setSent(result); else openDraft();
    }).catch(() => {});
  };
  const onOpenAnyway = () => { setSent(null); openDraft(); };
  const onDiscard = () => { setGuarding(false); openDraft(); };
  const onStay = () => { setGuarding(false); setSent(null); };

  return (
    <>
      <div className="banner" role="status">
        <span className="grow">Draft {draft.number}{draft.label ? ` (${draft.label})` : ''} has just been published from InDesign.</span>
        <button className="btn sm" onClick={onOpenClick} style={{ color: 'var(--ink)' }}>Open draft {draft.number}</button>
        <button className="btn sm ghost" onClick={onDismiss} style={{ color: 'var(--paper)' }}>Stay here</button>
      </div>
      {guarding && (
        <div className="card" style={{ margin: '0 16px 12px', padding: '12px 14px' }}>
          {sent ? (
            <>
              <div className="label">Sent to the designer</div>
              <div className="small">
                {sent.created.length === 0
                  ? 'No paragraph differed from the draft, so nothing was sent.'
                  : `${sent.created.length} change${sent.created.length === 1 ? '' : 's'} proposed: ${sent.created.map((c) => c.kind).join(', ')}.`}
              </div>
              {sent.conflicts.map((c) => (
                <div key={c.changeId} className="small muted">Also proposed by {c.authorName} on {paraLabel(c.paraId)}</div>
              ))}
              <div className="btn-row" style={{ marginTop: 8 }}>
                <button className="btn sm primary" onClick={onOpenAnyway}>Open draft {draft.number} anyway</button>
                <button className="btn sm ghost" onClick={onStay}>Stay</button>
              </div>
            </>
          ) : (
            <>
              <div className="label">You have unsent edits on draft {currentDraftNumber}</div>
              <div className="small muted">Send them to the designer, discard them, or stay on this draft.</div>
              <div className="btn-row" style={{ marginTop: 8 }}>
                <button className="btn sm primary" disabled={sending} onClick={onSendThenOpen}>{sending ? 'Sending' : 'Send'}</button>
                <button className="btn sm danger" disabled={sending} onClick={onDiscard}>Discard</button>
                <button className="btn sm ghost" onClick={onStay}>Stay</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
